/**
 * Support Ticket Types
 */

export type TicketStatus =
  | 'new'
  | 'open'
  | 'in_progress'
  | 'waiting_user'
  | 'resolved'
  | 'closed';

export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';

export type TicketSource = 'web' | 'ai_chat' | 'telegram' | 'email' | 'api';

export type ReplySenderType = 'user' | 'support' | 'system' | 'ai';

export interface SupportTicket {
  id: string;
  subject: string;
  message?: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: string;
  source?: TicketSource;
  created_at: string;
  updated_at: string;
  last_activity_at: string;
  first_response_at?: string;
  resolved_at?: string;
  satisfaction_rating?: number;
  metadata?: Record<string, unknown>;
  reply_count?: number;
  unread_count?: number;
}

export interface TicketReply {
  id: string;
  sender_type: ReplySenderType;
  content: string;
  attachments: Array<{
    id: string;
    name: string;
    url: string;
    type: string;
  }>;
  created_at: string;
}

export interface ListTicketsResponse {
  success: boolean;
  total: number;
  tickets: SupportTicket[];
  error?: string;
}

export interface TicketDetailResponse {
  success: boolean;
  ticket: SupportTicket;
  replies: TicketReply[];
  error?: string;
}

export interface CreateTicketParams {
  subject: string;
  message: string;
  category?: string;
  priority?: TicketPriority;
  metadata?: Record<string, unknown>;
}

export interface AddReplyParams {
  ticketId: string;
  content: string;
  attachments?: Array<{ name: string; url: string; type: string }>;
}

// Status display configuration
export const TICKET_STATUS_CONFIG: Record<TicketStatus, {
  label: string;
  color: 'default' | 'primary' | 'success' | 'warning' | 'danger';
  description: string;
}> = {
  new: {
    label: 'New',
    color: 'primary',
    description: 'Ticket just created',
  },
  open: {
    label: 'Open',
    color: 'primary',
    description: 'Awaiting support response',
  },
  in_progress: {
    label: 'In Progress',
    color: 'warning',
    description: 'Being worked on',
  },
  waiting_user: {
    label: 'Waiting for You',
    color: 'warning',
    description: 'Your response needed',
  },
  resolved: {
    label: 'Resolved',
    color: 'success',
    description: 'Issue resolved',
  },
  closed: {
    label: 'Closed',
    color: 'default',
    description: 'Ticket closed',
  },
};

export const TICKET_PRIORITY_CONFIG: Record<TicketPriority, {
  label: string;
  color: 'default' | 'primary' | 'warning' | 'danger';
}> = {
  low: { label: 'Low', color: 'default' },
  normal: { label: 'Normal', color: 'primary' },
  high: { label: 'High', color: 'warning' },
  urgent: { label: 'Urgent', color: 'danger' },
};
